import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useTeam } from './TeamContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  documentId
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { TEAM_ROLES } from '../data/nurseAppModels';
import { notificationModel } from '../data/notificationModel';

const ShiftPlanContext = createContext();

export const useShiftPlan = () => {
  const context = useContext(ShiftPlanContext);
  if (!context) {
    throw new Error('useShiftPlan must be used within ShiftPlanProvider');
  }
  return context;
};

export const ShiftPlanProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [plans, setPlans] = useState([]);
  const [planRequests, setPlanRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch plans for current team
  useEffect(() => {
    if (!currentTeam) {
      setPlans([]);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'shiftPlans'),
        where('teamId', '==', currentTeam.id),
        orderBy('createdAt', 'desc')
      ),
      (snapshot) => {
        const plansData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPlans(plansData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching plans:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentTeam]);

  // Fetch plan requests for current user
  useEffect(() => {
    if (!currentUser || !currentTeam || plans.length === 0) {
      setPlanRequests([]);
      return;
    }

    // Firestore 'in' query requires at least 1 and at most 10 items
    const planIds = plans.map(p => p.id).slice(0, 10);
    
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'shiftPlanRequests'),
        where('planId', 'in', planIds),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      ),
      (snapshot) => {
        const requestsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPlanRequests(requestsData);
      },
      (err) => {
        console.error('Error fetching plan requests:', err);
      }
    );

    return () => unsubscribe();
  }, [currentUser, currentTeam, plans]);

  // Generate plan ID (auto-increment index)
  const generatePlanId = async () => {
    // Get the latest plan ID
    const plansSnapshot = await getDocs(
      query(
        collection(db, 'shiftPlans'),
        orderBy('planId', 'desc'),
        limit(1)
      )
    );

    let planId = 1;
    if (!plansSnapshot.empty) {
      const latestPlan = plansSnapshot.docs[0].data();
      planId = (latestPlan.planId || 0) + 1;
    }

    return planId;
  };

  // Generate plan number and version
  const generatePlanNumberAndVersion = async (targetMonth, parentPlanId = null) => {
    const year = targetMonth.split('-')[0];
    const month = targetMonth.split('-')[1];
    
    let version = 1;
    let planNumber = `PLAN-${year}-${month}`;

    if (parentPlanId) {
      // This is a new version of existing plan
      // Get parent plan - query without orderBy to avoid index requirement
      const parentPlans = await getDocs(
        query(
          collection(db, 'shiftPlans'),
          where('planId', '==', parentPlanId)
        )
      );

      if (!parentPlans.empty) {
        // Sort by version in memory
        const plans = parentPlans.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => (b.version || 0) - (a.version || 0));
        
        if (plans.length > 0) {
          const parentPlan = plans[0];
          planNumber = parentPlan.planNumber;
          version = (parentPlan.version || 1) + 1;
        }
      }
    } else {
      // New plan - check for existing plans in this month
      // For new plans, always start with version 1
      // If there's an existing plan, it should be handled separately (user should create version instead)
      version = 1;
      planNumber = `PLAN-${year}-${month}`;
    }

    return { planNumber, version };
  };

  // Check if plan exists for target month
  const checkExistingPlan = async (targetMonth) => {
    try {
      const plansSnapshot = await getDocs(
        query(
          collection(db, 'shiftPlans'),
          where('teamId', '==', currentTeam.id),
          where('targetMonth', '==', targetMonth)
        )
      );

      // Filter original plans (not versions)
      const originalPlans = plansSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(plan => !plan.parentPlanId);

      return originalPlans.length > 0 ? originalPlans[0] : null;
    } catch (err) {
      console.error('Error checking existing plan:', err);
      return null;
    }
  };

  // Create Plan
  const createPlan = async (planData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check permissions (only Leader and Deputy Leaders can create plans)
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== TEAM_ROLES.LEADER && userTeamRole !== TEAM_ROLES.DEPUTY_LEADER) {
        throw new Error('คุณไม่มีสิทธิ์สร้างแผน');
      }

      // Check if plan exists for this month (only for new plans, not versions)
      if (!planData.parentPlanId) {
        const existingPlan = await checkExistingPlan(planData.targetMonth);
        if (existingPlan) {
          return {
            success: false,
            error: 'EXISTING_PLAN',
            existingPlan: existingPlan
          };
        }
      }

      // Check if plan exists for this month (only for new plans, not versions)
      if (!planData.parentPlanId) {
        const existingPlan = await checkExistingPlan(planData.targetMonth);
        if (existingPlan) {
          return {
            success: false,
            error: 'EXISTING_PLAN',
            existingPlan: existingPlan
          };
        }
      }

      // Generate plan ID (index)
      const planId = await generatePlanId();
      
      // Generate plan number and version
      const { planNumber, version } = await generatePlanNumberAndVersion(
        planData.targetMonth,
        planData.parentPlanId || null
      );

      // Get existing plans for this month to deactivate them (only if not a version)
      if (!planData.parentPlanId) {
        const existingPlansSnapshot = await getDocs(
          query(
            collection(db, 'shiftPlans'),
            where('teamId', '==', currentTeam.id),
            where('targetMonth', '==', planData.targetMonth),
            where('isActive', '==', true)
          )
        );

        // Filter and deactivate original plans (not versions)
        const originalPlans = existingPlansSnapshot.docs.filter(doc => {
          const data = doc.data();
          return !data.parentPlanId;
        });

        if (originalPlans.length > 0) {
          const batch = [];
          originalPlans.forEach(doc => {
            batch.push(updateDoc(doc.ref, {
              isActive: false,
              updatedAt: serverTimestamp()
            }));
          });
          await Promise.all(batch);
        }
      }

      // Calculate date range
      const targetDate = new Date(`${planData.targetMonth}-01`);
      const endDate = new Date(targetDate);
      endDate.setMonth(endDate.getMonth() + planData.monthsAhead);

      // Initialize calendar structure
      const calendar = {};
      const currentDate = new Date(targetDate);
      while (currentDate < endDate) {
        const dateKey = currentDate.toISOString().split('T')[0];
        calendar[dateKey] = {};
        currentDate.setDate(currentDate.getDate() + 1);
      }

      const newPlan = {
        planId, // Auto-increment index
        planNumber, // User-friendly identifier (PLAN-2025-12)
        planName: planData.planName || `แผนเวร ${planNumber}`, // User-defined name
        version, // Version number
        parentPlanId: planData.parentPlanId || null, // Reference to original plan (for versions)
        teamId: currentTeam.id,
        targetMonth: planData.targetMonth,
        monthsAhead: planData.monthsAhead,
        status: 'DRAFT',
        isActive: true, // New plan is always active
        planData: {
          calendar,
          totalShifts: 0,
          shiftsByUser: {},
          shiftsByType: {
            MORNING: 0,
            AFTERNOON: 0,
            NIGHT: 0
          }
        },
        submittedBy: null,
        submittedAt: null,
        submittedData: null,
        approvedBy: null,
        approvedAt: null,
        rejectionReason: null,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const planRef = await addDoc(collection(db, 'shiftPlans'), newPlan);

      // Send notification to all team members
      try {
        const teamMembers = currentTeam.members || [];
        const notificationsRef = collection(db, notificationModel.collection);
        
        for (const member of teamMembers) {
          if (member.uid !== currentUser.uid) {
            const notification = notificationModel.createNotification(
              member.uid,
              notificationModel.types.SHIFT_ASSIGNED,
              '📅 มีแผนเวรใหม่',
              `หัวหน้าทีมสร้างแผนเวร ${planNumber} แล้ว กรุณากรอกเวรที่ต้องการ`,
              {
                planId: planRef.id,
                teamId: currentTeam.id
              }
            );
            await addDoc(notificationsRef, {
              ...notification,
              read: false,
              readAt: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
      } catch (notifError) {
        console.error('Error sending notifications:', notifError);
      }

      return { success: true, planId: planRef.id, plan: { id: planRef.id, ...newPlan } };
    } catch (err) {
      console.error('Error creating plan:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Submit Plan Request (All team members can submit their shifts)
  const submitPlanRequest = async (planId, requestedShifts) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is a member of the team
      const isTeamMember = currentTeam.members?.some(
        member => member.uid === currentUser.uid
      ) || currentTeam.leaderId === currentUser.uid;

      if (!isTeamMember) {
        throw new Error('คุณไม่ใช่สมาชิกของทีมนี้');
      }

      const planRef = doc(db, 'shiftPlans', planId);
      const planSnap = await getDoc(planRef);

      if (!planSnap.exists()) {
        throw new Error('ไม่พบแผน');
      }

      const planData = planSnap.data();

      // Check if plan is in DRAFT or SUBMITTED status (can submit before approve)
      if (planData.status !== 'DRAFT' && planData.status !== 'SUBMITTED') {
        throw new Error('แผนนี้ไม่สามารถกรอกได้แล้ว');
      }

      // Check if user already submitted
      const existingRequest = await getDocs(
        query(
          collection(db, 'shiftPlanRequests'),
          where('planId', '==', planId),
          where('userId', '==', currentUser.uid)
        )
      );

      let requestRef;
      if (!existingRequest.empty) {
        // Update existing request
        requestRef = doc(db, 'shiftPlanRequests', existingRequest.docs[0].id);
        await updateDoc(requestRef, {
          requestedShifts,
          status: 'SUBMITTED',
          submittedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new request
        const newRequest = {
          planId,
          userId: currentUser.uid,
          userName: currentUser.displayName || currentUser.email,
          requestedShifts,
          status: 'SUBMITTED',
          submittedAt: serverTimestamp(),
          approvedAt: null,
          rejectionReason: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        requestRef = await addDoc(collection(db, 'shiftPlanRequests'), newRequest);
      }

      // Update plan status if first submission
      const allRequests = await getDocs(
        query(collection(db, 'shiftPlanRequests'), where('planId', '==', planId))
      );
      
      if (allRequests.size === 1) {
        await updateDoc(planRef, {
          status: 'SUBMITTED',
          submittedBy: currentUser.uid,
          submittedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      // Send notification to team leader
      try {
        const teamRef = doc(db, 'teams', currentTeam.id);
        const teamSnap = await getDoc(teamRef);
        if (teamSnap.exists()) {
          const team = teamSnap.data();
          if (team.leaderId && team.leaderId !== currentUser.uid) {
            const notificationsRef = collection(db, notificationModel.collection);
            const notification = notificationModel.createNotification(
              team.leaderId,
              notificationModel.types.SHIFT_SWAP_REQUEST, // Reuse for now, can add new type
              '📅 มีสมาชิกกรอกเวรแล้ว',
              `${currentUser.displayName || currentUser.email} กรอกเวรในแผน ${planData.planNumber} แล้ว`,
              {
                planId,
                requestId: requestRef.id,
                teamId: currentTeam.id
              }
            );
            await addDoc(notificationsRef, {
              ...notification,
              read: false,
              readAt: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }

      return { success: true, requestId: requestRef.id };
    } catch (err) {
      console.error('Error submitting plan request:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Plan (Only team creator/leader can approve)
  const approvePlan = async (planId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is the team creator (leaderId)
      // Get team data to check leaderId
      const teamRef = doc(db, 'teams', currentTeam.id);
      const teamSnap = await getDoc(teamRef);
      
      if (!teamSnap.exists()) {
        throw new Error('ไม่พบทีม');
      }

      const teamData = teamSnap.data();
      
      // Only team creator (leaderId) can approve
      if (teamData.leaderId !== currentUser.uid) {
        throw new Error('เฉพาะหัวหน้าทีมที่สร้างทีมเท่านั้นที่สามารถอนุมัติแผนได้');
      }

      const planRef = doc(db, 'shiftPlans', planId);
      const planSnap = await getDoc(planRef);

      if (!planSnap.exists()) {
        throw new Error('ไม่พบแผน');
      }

      const planData = planSnap.data();

      if (planData.status !== 'SUBMITTED') {
        throw new Error('แผนนี้ไม่อยู่ในสถานะที่สามารถอนุมัติได้');
      }

      // Get all submitted requests
      const requestsSnapshot = await getDocs(
        query(collection(db, 'shiftPlanRequests'), where('planId', '==', planId))
      );

      // Create shifts from submitted requests
      const shiftsRef = collection(db, 'shifts');
      const createdShifts = [];

      for (const requestDoc of requestsSnapshot.docs) {
        const requestData = requestDoc.data();
        if (requestData.status === 'SUBMITTED' && requestData.requestedShifts) {
          for (const shiftRequest of requestData.requestedShifts) {
            const date = new Date(shiftRequest.date);
            const shiftType = shiftRequest.shiftType;

            // Calculate start and end time based on shift type
            let startHour = 8;
            let endHour = 16;
            if (shiftType === 'AFTERNOON') {
              startHour = 16;
              endHour = 24;
            } else if (shiftType === 'NIGHT') {
              startHour = 0;
              endHour = 8;
            }

            const startTime = new Date(date);
            startTime.setHours(startHour, 0, 0, 0);
            const endTime = new Date(date);
            endTime.setHours(endHour, 0, 0, 0);

            const newShift = {
              teamId: currentTeam.id,
              planId,
              planNumber: planData.planNumber,
              planVersion: planData.version,
              shiftType,
              startTime: Timestamp.fromDate(startTime),
              endTime: Timestamp.fromDate(endTime),
              assignedTo: requestData.userId,
              assignedToName: requestData.userName,
              department: currentTeam.department || '',
              ward: '',
              notes: shiftRequest.notes || '',
              status: 'SCHEDULED',
              swapStatus: 'NONE',
              swapRequestId: null,
              clockInTime: null,
              clockOutTime: null,
              clockInLocation: null,
              createdBy: currentUser.uid,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };

            const shiftRef = await addDoc(shiftsRef, newShift);
            createdShifts.push(shiftRef.id);
          }
        }
      }

      // Update plan status
      await updateDoc(planRef, {
        status: 'APPROVED',
        approvedBy: currentUser.uid,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update all requests to APPROVED
      for (const requestDoc of requestsSnapshot.docs) {
        await updateDoc(doc(db, 'shiftPlanRequests', requestDoc.id), {
          status: 'APPROVED',
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      // Send notification to all team members
      try {
        const teamMembers = currentTeam.members || [];
        const notificationsRef = collection(db, notificationModel.collection);
        
        for (const member of teamMembers) {
          const notification = notificationModel.createNotification(
            member.uid,
            notificationModel.types.SHIFT_ASSIGNED,
            '✅ แผนเวรถูกอนุมัติแล้ว',
            `แผนเวร ${planData.planNumber} ถูกอนุมัติแล้ว เวรของคุณถูกสร้างแล้ว`,
            {
              planId,
              teamId: currentTeam.id
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      } catch (notifError) {
        console.error('Error sending notifications:', notifError);
      }

      return { success: true, createdShifts };
    } catch (err) {
      console.error('Error approving plan:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Reject Plan
  const rejectPlan = async (planId, reason) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check permissions
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== TEAM_ROLES.LEADER && userTeamRole !== TEAM_ROLES.DEPUTY_LEADER) {
        throw new Error('คุณไม่มีสิทธิ์ปฏิเสธแผน');
      }

      const planRef = doc(db, 'shiftPlans', planId);
      const planSnap = await getDoc(planRef);

      if (!planSnap.exists()) {
        throw new Error('ไม่พบแผน');
      }

      await updateDoc(planRef, {
        status: 'REJECTED',
        rejectionReason: reason || '',
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error rejecting plan:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Get plan by ID
  const getPlanById = async (planId) => {
    try {
      const planRef = doc(db, 'shiftPlans', planId);
      const planSnap = await getDoc(planRef);

      if (planSnap.exists()) {
        return {
          success: true,
          plan: { id: planSnap.id, ...planSnap.data() }
        };
      } else {
        return { success: false, error: 'ไม่พบแผน' };
      }
    } catch (err) {
      console.error('Error getting plan:', err);
      return { success: false, error: err.message };
    }
  };

  // Get plan requests by plan ID
  const getPlanRequests = async (planId) => {
    try {
      const requestsSnapshot = await getDocs(
        query(
          collection(db, 'shiftPlanRequests'),
          where('planId', '==', planId),
          orderBy('submittedAt', 'desc')
        )
      );

      return requestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (err) {
      console.error('Error getting plan requests:', err);
      return [];
    }
  };

  // Create new version of existing plan
  const createPlanVersion = async (parentPlanId, planName = null) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check permissions
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== TEAM_ROLES.LEADER && userTeamRole !== TEAM_ROLES.DEPUTY_LEADER) {
        throw new Error('คุณไม่มีสิทธิ์สร้าง version ใหม่');
      }

      // Get parent plan - query without orderBy to avoid index requirement
      const parentPlans = await getDocs(
        query(
          collection(db, 'shiftPlans'),
          where('planId', '==', parentPlanId)
        )
      );

      if (parentPlans.empty) {
        throw new Error('ไม่พบแผนต้นฉบับ');
      }

      // Sort by version in memory and get the latest
      const plans = parentPlans.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (b.version || 0) - (a.version || 0));

      const parentPlan = plans[0];
      const parentPlanDocId = plans[0].id;

      // Generate plan ID (new index)
      const planId = await generatePlanId();
      
      // Generate plan number and version
      const { planNumber, version } = await generatePlanNumberAndVersion(
        parentPlan.targetMonth,
        parentPlanId
      );

      // Copy calendar structure from parent
      const calendar = parentPlan.planData?.calendar || {};

      const newPlan = {
        planId, // New index
        planNumber, // Same as parent
        planName: planName || `${parentPlan.planName || planNumber} (Version ${version})`,
        version, // Incremented version
        parentPlanId: parentPlanId, // Reference to original plan
        teamId: currentTeam.id,
        targetMonth: parentPlan.targetMonth,
        monthsAhead: parentPlan.monthsAhead,
        status: 'DRAFT',
        isActive: true,
        planData: {
          calendar: { ...calendar }, // Copy calendar
          totalShifts: 0,
          shiftsByUser: {},
          shiftsByType: {
            MORNING: 0,
            AFTERNOON: 0,
            NIGHT: 0
          }
        },
        submittedBy: null,
        submittedAt: null,
        submittedData: null,
        approvedBy: null,
        approvedAt: null,
        rejectionReason: null,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const planRef = await addDoc(collection(db, 'shiftPlans'), newPlan);

      // Send notification to team members
      try {
        const teamMembers = currentTeam.members || [];
        const notificationsRef = collection(db, notificationModel.collection);
        
        for (const member of teamMembers) {
          if (member.uid !== currentUser.uid) {
            const notification = notificationModel.createNotification(
              member.uid,
              notificationModel.types.SHIFT_ASSIGNED,
              '📅 มีแผนเวร Version ใหม่',
              `หัวหน้าทีมสร้าง Version ${version} ของแผน ${planNumber} แล้ว`,
              {
                planId: planRef.id,
                parentPlanId: parentPlanId,
                teamId: currentTeam.id
              }
            );
            await addDoc(notificationsRef, {
              ...notification,
              read: false,
              readAt: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
      } catch (notifError) {
        console.error('Error sending notifications:', notifError);
      }

      return { success: true, planId: planRef.id, plan: { id: planRef.id, ...newPlan } };
    } catch (err) {
      console.error('Error creating plan version:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Get plan versions (all versions of a plan)
  const getPlanVersions = async (parentPlanId) => {
    try {
      // Query without orderBy to avoid index requirement, then sort in memory
      const versionsSnapshot = await getDocs(
        query(
          collection(db, 'shiftPlans'),
          where('parentPlanId', '==', parentPlanId)
        )
      );

      // Sort by version in memory
      const versions = versionsSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .sort((a, b) => (a.version || 0) - (b.version || 0));

      return versions;
    } catch (err) {
      console.error('Error getting plan versions:', err);
      return [];
    }
  };

  // Create plan version with approved swaps
  const createPlanVersionWithSwaps = async (planId, swapIds, planName = null) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Get original plan
      const planRef = doc(db, 'shiftPlans', planId);
      const planSnap = await getDoc(planRef);
      if (!planSnap.exists()) {
        throw new Error('ไม่พบแผน');
      }

      const originalPlan = planSnap.data();

      // Get approved swaps (Firestore 'in' query supports up to 10 items)
      const swapsRef = collection(db, 'shiftSwaps');
      const swapBatches = [];
      for (let i = 0; i < swapIds.length; i += 10) {
        swapBatches.push(swapIds.slice(i, i + 10));
      }

      const swaps = [];
      for (const batch of swapBatches) {
        const swapsSnapshot = await getDocs(
          query(
            swapsRef,
            where(documentId(), 'in', batch)
          )
        );
        swaps.push(...swapsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));
      }

      // Get all shifts from original plan
      const shiftsRef = collection(db, 'shifts');
      const shiftsSnapshot = await getDocs(
        query(
          shiftsRef,
          where('planId', '==', planId)
        )
      );

      const originalShifts = shiftsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply swaps to shifts
      const updatedShifts = originalShifts.map(shift => {
        // Check for shift-based swap (has originalShiftId)
        let swap = swaps.find(s => s.originalShiftId === shift.id);
        
        // If not found, check for plan-based swap (matches date and shiftType)
        if (!swap) {
          const shiftDate = shift.startTime?.toDate 
            ? shift.startTime.toDate().toISOString().split('T')[0]
            : shift.startTime?.seconds 
            ? new Date(shift.startTime.seconds * 1000).toISOString().split('T')[0]
            : null;
          
          swap = swaps.find(s => {
            // Plan-based swap: match by date and shiftType
            if (s.originalShift?.date && s.originalShift?.shiftType) {
              return s.originalShift.date === shiftDate && 
                     s.originalShift.shiftType === shift.shiftType &&
                     s.originalShift.userId === shift.assignedTo;
            }
            return false;
          });
        }
        
        if (swap) {
          // Swap assignedTo
          return {
            ...shift,
            assignedTo: swap.requestedTo,
            assignedToName: swap.requestedToName
          };
        }
        return shift;
      });

      // Get next version number
      // Query without orderBy to avoid index requirement, then sort in memory
      const versionsSnapshot = await getDocs(
        query(
          collection(db, 'shiftPlans'),
          where('parentPlanId', '==', originalPlan.planId || planId)
        )
      );

      let nextVersion = 1;
      if (!versionsSnapshot.empty) {
        // Sort by version in memory
        const versions = versionsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => (b.version || 0) - (a.version || 0));
        
        if (versions.length > 0) {
          const latestVersion = versions[0];
          nextVersion = (latestVersion.version || 1) + 1;
        }
      } else if (originalPlan.version) {
        nextVersion = originalPlan.version + 1;
      }

      // Create new plan version (approved immediately since swaps are already approved)
      const newPlan = {
        planId: originalPlan.planId,
        planNumber: originalPlan.planNumber,
        planName: planName || `${originalPlan.planName || originalPlan.planNumber} (Version ${nextVersion})`,
        version: nextVersion,
        parentPlanId: originalPlan.planId || planId,
        teamId: currentTeam.id,
        targetMonth: originalPlan.targetMonth,
        monthsAhead: originalPlan.monthsAhead,
        status: 'APPROVED', // Approved immediately since swaps are already approved
        isActive: true, // New version is active
        planData: originalPlan.planData,
        approvedBy: currentUser.uid,
        approvedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const newPlanRef = await addDoc(collection(db, 'shiftPlans'), newPlan);

      // Deactivate old versions of this plan
      const oldVersionsSnapshot = await getDocs(
        query(
          collection(db, 'shiftPlans'),
          where('planId', '==', originalPlan.planId),
          where('isActive', '==', true)
        )
      );

      // Deactivate all old versions
      for (const oldVersionDoc of oldVersionsSnapshot.docs) {
        if (oldVersionDoc.id !== newPlanRef.id) {
          await updateDoc(oldVersionDoc.ref, {
            isActive: false,
            updatedAt: serverTimestamp()
          });
        }
      }

      // Create new shifts with swapped assignments
      for (const shift of updatedShifts) {
        const newShift = {
          ...shift,
          id: undefined, // Remove old id
          planId: newPlanRef.id,
          planVersion: nextVersion,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        delete newShift.id;
        await addDoc(shiftsRef, newShift);
      }

      // Update swap statuses to APPROVED
      for (const swap of swaps) {
        const swapRef = doc(db, 'shiftSwaps', swap.id);
        await updateDoc(swapRef, {
          status: 'APPROVED',
          approvedBy: currentUser.uid,
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      // Send notification to team members
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const teamMembers = currentTeam.members || [];
        
        for (const member of teamMembers) {
          const notification = notificationModel.createNotification(
            member.uid,
            notificationModel.types.SHIFT_PLAN_VERSION_CREATED,
            '📅 แผนเวร version ใหม่',
            `แผน ${newPlan.planNumber} version ${nextVersion} ถูกสร้างแล้ว (มีการแลกเวร)`,
            {
              planId: newPlanRef.id,
              teamId: currentTeam.id
            }
          );
          await addDoc(notificationsRef, {
            ...notification,
            read: false,
            readAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      } catch (notifError) {
        console.error('Error sending notifications:', notifError);
      }

      return { success: true, planId: newPlanRef.id, version: nextVersion };
    } catch (err) {
      console.error('Error creating plan version with swaps:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Request Plan-Based Swap
  const requestPlanSwap = async (planId, swapData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Get plan
      const planRef = doc(db, 'shiftPlans', planId);
      const planSnap = await getDoc(planRef);
      if (!planSnap.exists()) {
        throw new Error('ไม่พบแผน');
      }

      const planData = planSnap.data();

      // Create plan-based swap request
      const swapRequest = {
        teamId: currentTeam.id,
        planId: planId,
        swapType: swapData.swapType, // 'SAME_DAY', 'DIFFERENT_DAY', 'COVER_REQUEST'
        originalShift: {
          date: swapData.date, // YYYY-MM-DD
          shiftType: swapData.shiftType,
          userId: currentUser.uid,
          userName: currentUser.displayName || currentUser.email
        },
        requestedShift: swapData.requestedShift ? {
          date: swapData.requestedShift.date,
          shiftType: swapData.requestedShift.shiftType,
          userId: swapData.requestedShift.userId,
          userName: swapData.requestedShift.userName
        } : null,
        requestedBy: currentUser.uid,
        requestedByName: currentUser.displayName || currentUser.email,
        requestedTo: swapData.requestedTo,
        requestedToName: swapData.requestedToName,
        message: swapData.message || '',
        status: 'PENDING',
        memberApprovedAt: null,
        memberRejectedAt: null,
        memberRejectionReason: null,
        leaderApprovedAt: null,
        leaderRejectedAt: null,
        leaderRejectionReason: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const swapRef = await addDoc(collection(db, 'shiftSwaps'), swapRequest);

      // Send notification to requested user
      try {
        const notificationsRef = collection(db, notificationModel.collection);
        const notification = notificationModel.createNotification(
          swapData.requestedTo,
          notificationModel.types.SHIFT_SWAP_REQUEST,
          '🔄 มีคำขอแลกเวร',
          `${currentUser.displayName || currentUser.email} ขอแลกเวรกับคุณ`,
          {
            swapId: swapRef.id,
            planId: planId,
            teamId: currentTeam.id
          }
        );
        await addDoc(notificationsRef, {
          ...notification,
          read: false,
          readAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }

      return { success: true, swapId: swapRef.id };
    } catch (err) {
      console.error('Error requesting plan swap:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const value = {
    plans,
    planRequests,
    loading,
    error,
    createPlan,
    createPlanVersion,
    createPlanVersionWithSwaps,
    checkExistingPlan,
    submitPlanRequest,
    approvePlan,
    rejectPlan,
    getPlanById,
    getPlanRequests,
    getPlanVersions,
    requestPlanSwap
  };

  return (
    <ShiftPlanContext.Provider value={value}>
      {children}
    </ShiftPlanContext.Provider>
  );
};

